import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableSortLabel,
  TextField,
  Box,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { auth } from './firebaseConfig';
import { collection } from 'firebase/firestore';
import { toTurkishUpperCase } from '../utils/searchUtils';

const StyledTableContainer = styled(TableContainer)({
  maxHeight: '400px',
  width: '100%',
  overflowX: 'auto', // Yatay kaydırma ekle
  '& .MuiTableCell-root': {
    padding: '8px 12px', // Padding'i biraz azalt
  },
  '& .MuiTable-root': {
    tableLayout: 'fixed', // Sabit tablo düzeni
    minWidth: '800px', // Minimum genişlik
  }
});

const StyledTableCell = styled(TableCell)({
  whiteSpace: 'normal', // Alt satıra geçmeye izin ver
  overflow: 'hidden',
  wordWrap: 'break-word', // Uzun kelimeleri böl
  lineHeight: '1.3', // Satır yüksekliğini ayarla
  verticalAlign: 'top', // Üstten hizala
});

const FilterBox = styled(Box)({
  padding: '16px',
  backgroundColor: '#fff',
});

// Müşteri adı için özel hücre stili
const CustomerNameCell = styled(TableCell)({
  whiteSpace: 'normal',
  overflow: 'hidden',
  width: '35%', // Genişlik yüzdesi
  wordWrap: 'break-word',
  lineHeight: '1.3',
  padding: '12px 16px',
  verticalAlign: 'top',
});

// Cari kod için özel hücre stili
const CariCodeCell = styled(TableCell)({
  whiteSpace: 'normal',
  overflow: 'hidden',
  width: '15%', // Genişlik yüzdesi
  wordWrap: 'break-word',
  lineHeight: '1.3',
  verticalAlign: 'top',
});

// Durum için özel hücre stili
const StatusCell = styled(TableCell)({
  whiteSpace: 'normal',
  overflow: 'hidden',
  width: '15%', // Genişlik yüzdesi
  wordWrap: 'break-word',
  lineHeight: '1.3',
  verticalAlign: 'top',
});

// Telefon için özel hücre stili
const PhoneCell = styled(TableCell)({
  whiteSpace: 'normal',
  overflow: 'hidden',
  width: '17.5%', // Genişlik yüzdesi
  wordWrap: 'break-word',
  lineHeight: '1.3',
  textAlign: 'right',
  verticalAlign: 'top',
});

// E-posta için özel hücre stili
const EmailCell = styled(TableCell)({
  whiteSpace: 'normal',
  overflow: 'hidden',
  width: '17.5%', // Genişlik yüzdesi
  wordWrap: 'break-word',
  lineHeight: '1.3',
  textAlign: 'right',
  verticalAlign: 'top',
});

const CustomerSelectionTable = ({ customers = [], onSelectCustomer, selectedId, showOnlyShantiyeler = false, enableAutoUppercase = false }) => {
  const [orderBy, setOrderBy] = useState('cariCode');
  const [order, setOrder] = useState('desc');
  const [filter, setFilter] = useState('');

  const userEmail = auth.currentUser?.email;
  if (!userEmail) {
    return null;
  }

  // Müşterileri ve şantiyeleri grupla
  const groupedCustomers = customers.reduce((acc, customer) => {
    // Eğer sadece şantiyeler gösterilecekse, sadece şantiyeleri dahil et
    if (showOnlyShantiyeler && !customer.Şantiye) {
      return acc;
    }
    
    if (!customer.parentId) {
      if (!acc[customer.id]) {
        acc[customer.id] = {
          parent: customer,
          children: []
        };
      } else {
        acc[customer.id].parent = customer;
      }
    } else {
      if (!acc[customer.parentId]) {
        acc[customer.parentId] = {
          parent: null,
          children: [customer]
        };
      } else {
        acc[customer.parentId].children.push(customer);
      }
    }
    return acc;
  }, {});

  // Cari kod sıralama fonksiyonu
  const sortByCariCode = (a, b, isAscending = true) => {
    const cariCodeA = (a.cariCode || '').toString().trim();
    const cariCodeB = (b.cariCode || '').toString().trim();
    
    // Boş değerler en sona
    if (!cariCodeA && !cariCodeB) return 0;
    if (!cariCodeA) return 1;
    if (!cariCodeB) return -1;
    
    // A-002, B-001 gibi formatlar için özel sıralama
    const parseCode = (code) => {
      const parts = code.split('-');
      if (parts.length === 2) {
        const letter = parts[0].trim();
        const number = parseInt(parts[1].trim());
        return { letter, number: isNaN(number) ? 0 : number };
      }
      return { letter: code, number: 0 };
    };
    
    const parsedA = parseCode(cariCodeA);
    const parsedB = parseCode(cariCodeB);
    
    // Önce harf kısmını karşılaştır
    const letterCompare = parsedA.letter.localeCompare(parsedB.letter, 'tr');
    if (letterCompare !== 0) {
      return isAscending ? letterCompare : -letterCompare;
    }
    
    // Harf kısımları aynıysa sayı kısmını karşılaştır
    const numberCompare = parsedA.number - parsedB.number;
    return isAscending ? numberCompare : -numberCompare;
  };

  // Sıralama fonksiyonu
  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Düzenlenmiş ve filtrelenmiş listeyi oluştur
  const sortedCustomers = [];
  
  // Önce ana müşterileri sırala
  const parentCustomers = Object.values(groupedCustomers)
    .map(({ parent }) => parent)
    .filter(Boolean) // null değerleri filtrele
    .sort((a, b) => {
      if (orderBy === 'cariCode') {
        return sortByCariCode(a, b, order === 'asc');
      }
      const comparison = (a[orderBy] || '').localeCompare(b[orderBy] || '');
      return order === 'asc' ? comparison : -comparison;
    });

  // Sıralanmış ana müşterilere göre listeyi oluştur
  parentCustomers.forEach(parent => {
    const children = groupedCustomers[parent.id]?.children || [];
    
    const parentMatchesFilter = 
      parent['Müşteri Adı']?.toLowerCase().includes(filter.toLowerCase()) ||
      parent.cariCode?.toLowerCase().includes(filter.toLowerCase());

    const filteredChildren = children.filter(child => 
      filter === '' || 
      child['Müşteri Adı']?.toLowerCase().includes(filter.toLowerCase()) ||
      child.cariCode?.toLowerCase().includes(filter.toLowerCase())
    );

    if (parentMatchesFilter || filteredChildren.length > 0) {
      // Ana müşteriyi ekle
      sortedCustomers.push({
        ...parent,
        isParent: true
      });

      // Şantiyeleri cari koda göre sırala ve ekle
      filteredChildren
        .sort((a, b) => sortByCariCode(a, b, false)) // büyükten küçüğe
        .forEach(child => {
          sortedCustomers.push({
            ...child,
            isChild: true
          });
        });
    }
  });

  return (
    <>
      <FilterBox>
        <TextField
          fullWidth
          size="small"
          variant="outlined"
          placeholder="Müşteri adı veya cari kod ile filtrele..."
          value={filter}
          onChange={(e) => {
            const value = enableAutoUppercase ? toTurkishUpperCase(e.target.value) : e.target.value;
            setFilter(value);
          }}
        />
      </FilterBox>
      <StyledTableContainer component={Paper}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <CustomerNameCell component="th">
                <TableSortLabel
                  active={orderBy === 'Müşteri Adı'}
                  direction={orderBy === 'Müşteri Adı' ? order : 'asc'}
                  onClick={() => handleSort('Müşteri Adı')}
                >
                  Müşteri/Şantiye Adı
                </TableSortLabel>
              </CustomerNameCell>
              <CariCodeCell component="th">
                <TableSortLabel
                  active={orderBy === 'cariCode'}
                  direction={orderBy === 'cariCode' ? order : 'asc'}
                  onClick={() => handleSort('cariCode')}
                >
                  Cari Kod
                </TableSortLabel>
              </CariCodeCell>
              <StatusCell component="th">Durum</StatusCell>
              <PhoneCell component="th" align="right">Telefon</PhoneCell>
              <EmailCell component="th" align="right">E-posta</EmailCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedCustomers.map((customer) => (
              <TableRow
                key={customer.id}
                onClick={() => onSelectCustomer(customer)}
                sx={{
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: '#f5f5f5',
                  },
                  // Seçili satır için özel stil
                  ...(selectedId === customer.id && {
                    backgroundColor: '#e3f2fd',
                    borderLeft: '4px solid #1976d2',
                    '&:hover': {
                      backgroundColor: '#e3f2fd',
                    }
                  })
                }}
              >
                <CustomerNameCell>
                  {customer['Müşteri Adı']}
                  {customer.isChild && ' (Şantiye)'}
                </CustomerNameCell>
                <CariCodeCell>{customer.cariCode}</CariCodeCell>
                <StatusCell>
                  <span style={{
                    padding: '4px 8px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    color: customer.status === 'Onaylandı' ? '#2e7d32' : '#f57c00',
                    backgroundColor: customer.status === 'Onaylandı' ? '#e8f5e8' : '#fff3e0',
                    border: `1px solid ${customer.status === 'Onaylandı' ? '#c8e6c9' : '#ffcc02'}`
                  }}>
                    {customer.status || customer.Onay || 'Onay Bekliyor'}
                  </span>
                </StatusCell>
                <PhoneCell align="right">{customer.Telefon}</PhoneCell>
                <EmailCell align="right">{customer['E-posta']}</EmailCell>
              </TableRow>
            ))}
            {sortedCustomers.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  Kayıt Bulunamadı
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </StyledTableContainer>
    </>
  );
};

export default CustomerSelectionTable;